import { useEffect } from 'react';
import { keyboardInset } from './keyboardInset';

/**
 * Put the document back at the top whenever something has moved it.
 *
 * THE DOCUMENT IS NOT MEANT TO SCROLL AT ALL — `body { overflow: hidden }` and one scrolling
 * `<main>` (see styles.css). But `overflow: hidden` only stops a FINGER scrolling it; the browser
 * still can. iOS pans the document to bring a focused field above the keyboard, and does not
 * always pan it back when the keyboard goes — and from then on the top of the page, the title in
 * the top bar included, is above the screen with no gesture that reaches it, because the only
 * thing a swipe scrolls is `<main>`, which is already at its own top. It stays that way across
 * navigation, so it turns up on a page with no field on it.
 *
 * LEFT ALONE WHILE THE KEYBOARD IS UP, when the pan is what keeps the field being typed in
 * visible, and while the page is pinch-zoomed, when moving about the document is the point.
 */
export function useDocumentHeldAtTop(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    const settle = () => {
      if (window.scrollX === 0 && window.scrollY === 0) return;
      if (keyboardInset() > 0) return;
      if (viewport && Math.abs(viewport.scale - 1) > 0.01) return;
      window.scrollTo(0, 0);
    };
    // After the blur: the keyboard has not begun to leave when `focusout` is dispatched, and the
    // viewport's own `resize` — which normally catches it — is not sent by every iOS version.
    let timer: number | undefined;
    const settleSoon = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, 350);
    };

    window.addEventListener('scroll', settle, { passive: true });
    document.addEventListener('focusout', settleSoon);
    viewport?.addEventListener('resize', settle);
    settle();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', settle);
      document.removeEventListener('focusout', settleSoon);
      viewport?.removeEventListener('resize', settle);
    };
  }, []);
}
